/*
 * Periods for stats ("This quarter", "Last financial year", a custom range): days from and to, both
 * included, as "YYYY-MM-DD" in the business's own days. The choices follow accounting software
 * (Xero's): this, last and to date, by month, quarter and financial year. Quarters follow the
 * financial year, which starts in a month that depends on the country (financialYearStart).
 * Shared by the server and the browser: no imports.
 */

export interface Period {
    /** First and last day, both included ("YYYY-MM-DD"). */
    from: string;
    to: string;
}

export type PresetId =
    | "last12"
    | "thisMonth"
    | "thisQuarter"
    | "thisYear"
    | "lastMonth"
    | "lastQuarter"
    | "lastYear"
    | "monthToDate"
    | "quarterToDate"
    | "yearToDate";

/** Ready-made periods, in groups (a line between each), as they're offered. */
export const PRESET_GROUPS: readonly (readonly PresetId[])[] = [
    ["last12"],
    ["thisMonth", "thisQuarter", "thisYear"],
    ["lastMonth", "lastQuarter", "lastYear"],
    ["monthToDate", "quarterToDate", "yearToDate"],
];

export const PRESET_LABELS: Record<PresetId, string> = {
    last12: "Last 12 months",
    thisMonth: "This month",
    thisQuarter: "This quarter",
    thisYear: "This financial year",
    lastMonth: "Last month",
    lastQuarter: "Last quarter",
    lastYear: "Last financial year",
    monthToDate: "Month to date",
    quarterToDate: "Quarter to date",
    yearToDate: "Year to date",
};

/** Months the financial year starts in, by country (1 = January); anywhere else, the calendar year. */
const FINANCIAL_YEAR_START: Record<string, number> = { AU: 7, NZ: 4, GB: 4, IN: 4, JP: 4, HK: 4, ZA: 3, PK: 7, EG: 7, KE: 7 };

/** The month a country's financial year starts in (1-12): January when not known. */
export const financialYearStart = (country: string | null) => (country ? (FINANCIAL_YEAR_START[country] ?? 1) : 1);

const pad = (n: number) => String(n).padStart(2, "0");
/** A date as "YYYY-MM-DD" (its own day, not UTC's). */
export const dayOf = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
/** "YYYY-MM-DD" as a date (midnight, local). */
export const dateOf = (day: string) => new Date(`${day}T00:00:00`);
/** The first day of the month `months` after `year`/`month` (0-based month; any number of months). */
const monthStart = (year: number, month: number, months = 0) => new Date(year, month + months, 1);
/** The last day before a date. */
const dayBefore = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1);

/** A preset's days, from `today` (and the month the financial year starts in, 1-12). */
export function presetPeriod(id: PresetId, today: Date, fyStart = 1): Period {
    const y = today.getFullYear();
    const m = today.getMonth();
    // The financial year and quarter `today` is in: how many months into the year, then each start.
    const intoYear = (m - (fyStart - 1) + 12) % 12;
    const yearStart = monthStart(y, m, -intoYear);
    const quarterStart = monthStart(y, m, -(intoYear % 3));
    const span = (from: Date, months: number): Period => ({ from: dayOf(from), to: dayOf(dayBefore(monthStart(from.getFullYear(), from.getMonth(), months))) });
    const toDate = (from: Date): Period => ({ from: dayOf(from), to: dayOf(today) });
    switch (id) {
        case "last12":
            // This month and the 11 before (as the Materials list's "not used in 12 months").
            return { from: dayOf(monthStart(y, m, -11)), to: dayOf(today) };
        case "thisMonth":
            return span(monthStart(y, m), 1);
        case "thisQuarter":
            return span(quarterStart, 3);
        case "thisYear":
            return span(yearStart, 12);
        case "lastMonth":
            return span(monthStart(y, m, -1), 1);
        case "lastQuarter":
            return span(monthStart(quarterStart.getFullYear(), quarterStart.getMonth(), -3), 3);
        case "lastYear":
            return span(monthStart(yearStart.getFullYear(), yearStart.getMonth(), -12), 12);
        case "monthToDate":
            return toDate(monthStart(y, m));
        case "quarterToDate":
            return toDate(quarterStart);
        case "yearToDate":
            return toDate(yearStart);
    }
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;
/** A real day ("2026-02-30" isn't). */
export const isDay = (s: string) => DAY.test(s) && dayOf(dateOf(s)) === s;

/** Days in a period, both ends counted. */
export const daysIn = (p: Period) => Math.round((dateOf(p.to).getTime() - dateOf(p.from).getTime()) / 86_400_000) + 1;

/** The longest period stats are worked out for: about 10 years. */
export const MAX_PERIOD_DAYS = 3660;

/** Days that make a period stats can be asked for: both real, in order, and at most MAX_PERIOD_DAYS. */
export const isPeriod = (from: string, to: string) => isDay(from) && isDay(to) && from <= to && daysIn({ from, to }) <= MAX_PERIOD_DAYS;

/**
 * A period as written (`locale`: the business's): a whole month "Oct 2026", else "1 Oct - 31 Dec
 * 2026", with each end's year only when they differ ("1 Jul 2026 - 30 Jun 2027").
 */
export function periodText(p: Period, locale?: string | null): string {
    const from = dateOf(p.from);
    const to = dateOf(p.to);
    const loc = locale ?? undefined;
    const wholeMonth = from.getDate() === 1 && dayOf(dayBefore(monthStart(from.getFullYear(), from.getMonth(), 1))) === p.to;
    if (wholeMonth) return from.toLocaleDateString(loc, { month: "short", year: "numeric" });
    const sameYear = from.getFullYear() === to.getFullYear();
    const fromText = from.toLocaleDateString(loc, { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) });
    const toText = to.toLocaleDateString(loc, { day: "numeric", month: "short", year: "numeric" });
    return p.from === p.to ? toText : `${fromText} - ${toText}`;
}

/** How a period's use is shown as bars: by day (a month or less), by week (about a quarter or less), else by month. */
export type BarUnit = "day" | "week" | "month";
export function barUnit(p: Period): BarUnit {
    const days = daysIn(p);
    if (days <= 31) return "day";
    return days <= 98 ? "week" : "month";
}

/** The day `days` after one (before, when negative), as calendar days: right across a change of clocks (unlike adding 24 hours). */
export function addDays(day: string, days: number) {
    const d = dateOf(day);
    return dayOf(new Date(d.getFullYear(), d.getMonth(), d.getDate() + days));
}

/** The first day of the bar after one starting on `d`. */
function nextBar(d: Date, unit: BarUnit) {
    if (unit === "month") return monthStart(d.getFullYear(), d.getMonth(), 1);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate() + (unit === "day" ? 1 : 7));
}

/** The bars' first days across a period, oldest first: each day, each 7 days from its start, or each month (the first partly). */
export function barStarts(p: Period): string[] {
    const unit = barUnit(p);
    const starts: string[] = [];
    const end = dateOf(p.to).getTime();
    let d = dateOf(p.from);
    while (d.getTime() <= end) {
        starts.push(dayOf(d));
        d = nextBar(d, unit);
    }
    return starts;
}
